import { Link } from "react-router-dom";
import "./style.css";
import Moment from "react-moment";
import { Dots } from "../../svg";
import ReactsPopup from "./ReactsPopup";
import { useEffect, useRef, useState, useCallback } from "react";
import CreateComment from "./CreateComment";
import PostMenu from "./PostMenu";
import PostFeelingActivity from "./PostFeelingActivity";
import { getReacts, reactPost } from "../../functions/post";
import Comment from "./Comment";
import { useSocketContext } from "../../context/SocketContext";
import {
  FaWhatsapp,
  FaTwitter,
  // FaInstagram,
  FaLinkedin,
  FaTelegram,
  FaTimes,
} from "react-icons/fa";
import TaggedFriendsModal from "./TaggedFriendsModal";
import FullScreenPostViewer from "./FullScreenPostViewer";

// Helper function to extract event name from text (removes icon if present)
const extractEventName = (text) => {
  if (!text) return "";
  // Remove emoji/icon if present (check if text starts with emoji followed by space)
  return text.replace(/^[\u{1F300}-\u{1F9FF}]+\s*/u, '').trim() || text.trim();
};

// Helper function to extract icon from text (if present)
const extractIcon = (text) => {
  if (!text) return "";
  const match = text.match(/^([\u{1F300}-\u{1F9FF}]+)\s*/u);
  return match ? match[1] : "";
};

// Helper function to get icon for life events based on title
const getLifeEventIcon = (eventName) => {
  // If eventName already contains an icon, extract and return it
  const icon = extractIcon(eventName);
  if (icon) return icon;
  
  // Otherwise, determine icon from event name
  const eventNameOnly = extractEventName(eventName);
  const titleToCheck = eventNameOnly.trim();
  
  // Map of specific titles to their icons (title-specific mapping)
  const titleToIconMap = {
    // Work
    "New Job": "🆕💼",
   "Promotion": "📈",
   "Left Job": "📤",
   "Retirement": "🎉",
   "Work Anniversary": "🎂",
   "Started New Project": "🚀",
    
    // Education
      "Graduated": "🎓",
      "Started School": "🏫",
      "Got a Degree": "📜",
      "Finished Course": "📚",
      "Achieved Certification": "🏅",
      "Enrolled in Program": "📝",
    
    // Relationship
   "Started Relationship": "❤️",
   "In a Relationship": "💑",
   "Got Engaged": "💍",
   "Got Married": "👰🤵",
   "Anniversary": "💕",
   "It's Complicated": "😕",
   "Single": "🙂",
   "Divorced": "💔",

    // Home
    "Moved": "🏠",
    "Bought a Home": "🏡",
    "Sold a Home": "🏠",
    "Renovated": "🔨",
    "New Roommate": "👥",
    "New Pet": "🐾",
    
    // Family
    "New Family Member": "👶",
    "Family Member's Birthday": "🎂",
    "Family Reunion": "👨‍👩‍👧‍👦",
    "Family Milestone": "👨‍👩‍👧‍👦",
    "Family Celebration": "🎉",
    
    // Travel
    "New City": "🌆",
    "New Country": "🌍",
    "Vacation": "✈️",
    "Backpacking": "🎒",
    "Road Trip": "🚗",
    "Adventure": "🏔️",
    
    // Interests
    "New Hobby": "⭐",
    "Achievement": "🏆",
    "Competition": "🥇",
    "Event": "🎪",
    "New Skill": "🎯",
    "Milestone": "🎖️",
    
    // Health
    "Fitness Goal": "💪",
    "Health Milestone": "💚",
    "Wellness Achievement": "🧘",
    "Recovery": "🏥",
    "Health Update": "💊",
    
    // Milestones
    "Personal Achievement": "🏆",
    "Career Milestone": "📈",
    "Life Milestone": "🌟",
    "Award": "🏅",
    "Recognition": "👏",
    "Accomplishment": "✅",
    
    // Remembrance
    "Memorial": "🕯️",
    "Tribute": "🌹",
    "In Memory": "🖤",
    "Remembrance": "🎗️",
  };
  
  // Check exact match first
  if (titleToIconMap[titleToCheck]) {
    return titleToIconMap[titleToCheck];
  }
  
  // Check case-insensitive match
  const lowerTitle = titleToCheck.toLowerCase();
  for (const [key, icon] of Object.entries(titleToIconMap)) {
    if (key.toLowerCase() === lowerTitle) {
      return icon;
    }
  }
  
  // Fallback: determine by category if title matches known events
  const lifeEventsByCategory = {
    work: ["New Job", "Promotion", "Left Job", "Retirement", "Work Anniversary", "Started New Project"],
    education: ["Graduated", "Started School", "Got a Degree", "Finished Course", "Achieved Certification", "Enrolled in Program"],
    relationship: ["Started Relationship", "Got Engaged", "Got Married", "In a Relationship", "It's Complicated", "Single", "Anniversary", "Divorced"],
    home: ["Moved", "Bought a Home", "Sold a Home", "Renovated", "New Roommate", "New Pet"],
    family: ["New Family Member", "Family Member's Birthday", "Family Reunion", "Family Milestone", "Family Celebration"],
    travel: ["New City", "New Country", "Vacation", "Backpacking", "Road Trip", "Adventure"],
    interests: ["New Hobby", "Achievement", "Competition", "Event", "New Skill", "Milestone"],
    health: ["Fitness Goal", "Health Milestone", "Wellness Achievement", "Recovery", "Health Update"],
    milestones: ["Personal Achievement", "Career Milestone", "Life Milestone", "Award", "Recognition", "Accomplishment"],
    remembrance: ["Memorial", "Tribute", "In Memory", "Remembrance"],
  };

  const categoryIcons = {
    work: "💼",
    education: "🎓",
    relationship: "❤️",
    home: "🏠",
    family: "👨‍👩‍👧‍👦",
    travel: "🌍",
    interests: "⭐",
    health: "💚",
    milestones: "🏆",
    remembrance: "🕯️",
    custom: "🚩",
  };

  // Check if title matches any known event
  for (const [category, events] of Object.entries(lifeEventsByCategory)) {
    if (events.some(event => event.toLowerCase() === lowerTitle)) {
      return categoryIcons[category];
    }
  }
  
  // Default icon for custom events
  return categoryIcons.custom;
};

// Helper function to check if post text is a life event name
const isLifeEventName = (text) => {
  if (!text || !text.trim()) return false;
  
  const lifeEventsByCategory = {
    work: ["New Job", "Promotion", "Left Job", "Retirement", "Work Anniversary", "Started New Project"],
    education: ["Graduated", "Started School", "Got a Degree", "Finished Course", "Achieved Certification", "Enrolled in Program"],
    relationship: ["Started Relationship", "Got Engaged", "Got Married", "In a Relationship", "It's Complicated", "Single", "Anniversary", "Divorced"],
    home: ["Moved", "Bought a Home", "Sold a Home", "Renovated", "New Roommate", "New Pet"],
    family: ["New Family Member", "Family Member's Birthday", "Family Reunion", "Family Milestone", "Family Celebration"],
    travel: ["New City", "New Country", "Vacation", "Backpacking", "Road Trip", "Adventure"],
    interests: ["New Hobby", "Achievement", "Competition", "Event", "New Skill", "Milestone"],
    health: ["Fitness Goal", "Health Milestone", "Wellness Achievement", "Recovery", "Health Update"],
    milestones: ["Personal Achievement", "Career Milestone", "Life Milestone", "Award", "Recognition", "Accomplishment"],
    remembrance: ["Memorial", "Tribute", "In Memory", "Remembrance"],
  };

  const trimmedText = text.trim();
  // Remove emoji/icon if present (check if text starts with emoji followed by space)
  const textWithoutIcon = trimmedText.replace(/^[\u{1F300}-\u{1F9FF}]+\s*/u, '').trim();
  const textToCheck = textWithoutIcon || trimmedText;
  
  for (const events of Object.values(lifeEventsByCategory)) {
    if (events.includes(textToCheck)) {
      return true;
    }
  }
  return false;
};

// Helper function to check if a post is a life event post (exported for FullScreenPostViewer)
export const isLifeEventPost = (post) => {
  // Check if post has text but no images/videos and no background
  const hasText = post.text && post.text.trim();
  const hasNoMedia = !post.images || post.images.length === 0;
  const hasNoBackground = !post.background;
  const hasNoType = post.type === null;
  
  return hasText && hasNoMedia && hasNoBackground && hasNoType && !post.feeling && !post.activity && isLifeEventName(post.text);
};

export default function Post({ post, user, profile, gridView = false, onPostDeleted, isGroupPost = false, groupName, disableFullScreen = false, hideComments = false, onCommentsChange, commentInputFocusRef, isSavedView = false, onUnsave }) {
  const { socket, isConnected } = useSocketContext();
  const [visible, setVisible] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [reacts, setReacts] = useState();
  const [check, setCheck] = useState();
  const [total, setTotal] = useState(0);
  // show only one comment initially; rest will be revealed when user clicks "View more comments"
  const [count, setCount] = useState(1);
  const [checkSaved, setCheckSaved] = useState();
  const [comments, setComments] = useState([]);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [showTaggedModal, setShowTaggedModal] = useState(false);
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  // Handle keyboard navigation for image fullscreen
  useEffect(() => {
    if (selectedImageIndex !== null && post.images) {
      const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
          setSelectedImageIndex(null);
        } else if (e.key === 'ArrowLeft' && post.images.length > 1) {
          setSelectedImageIndex((prev) => 
            prev > 0 ? prev - 1 : post.images.length - 1
          );
        } else if (e.key === 'ArrowRight' && post.images.length > 1) {
          setSelectedImageIndex((prev) => 
            prev < post.images.length - 1 ? prev + 1 : 0
          );
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [selectedImageIndex, post.images]);

  const getPostReacts = useCallback(async () => {
    const res = await getReacts(post._id, user.token);
    setReacts(res.reacts);
    setCheck(res.check);
    setTotal(res.total);
    setCheckSaved(res.checkSaved);
  }, [post._id, user.token]);

  useEffect(() => {
    getPostReacts();
  }, [post, getPostReacts]);

  useEffect(() => {
    setComments(post?.comments || []);
  }, [post]);

  // Real-time comments/replies: update when someone else adds a comment or reply
  const postIdStr = post?._id ? String(post._id) : '';
  useEffect(() => {
    if (!socket || !isConnected || !postIdStr || !Array.isArray(comments)) return;
    const onCommentAdded = (data) => {
      if (String(data.postId) !== postIdStr) return;
      if (data.comments && Array.isArray(data.comments)) setComments(data.comments);
    };
    const onReplyAdded = (data) => {
      if (String(data.postId) !== postIdStr) return;
      if (data.comments && Array.isArray(data.comments)) setComments(data.comments);
    };
    socket.on('comment-added', onCommentAdded);
    socket.on('reply-added', onReplyAdded);
    return () => {
      socket.off('comment-added', onCommentAdded);
      socket.off('reply-added', onReplyAdded);
    };
  }, [socket, isConnected, postIdStr, comments]);

  // Notify parent of comments changes if callback provided
  useEffect(() => {
    if (onCommentsChange) {
      onCommentsChange(comments, setComments, count, setCount);
    }
  }, [comments, count, onCommentsChange]);

  // Handler called by child Comment components when they are deleted (optimistic)
  const handleCommentDelete = (deletedCommentId) => {
    // If null is passed, child signals a revert or failure — ignore here
    if (!deletedCommentId) return;

    setComments((prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.filter((c) => (c._id || c.id || c.tempId) !== deletedCommentId);
    });

    // Decrease the visible comments count safely
    setCount((prev) => (prev && prev > 0 ? prev - 1 : 0));
  };

  const reactHandler = async (type) => {
    reactPost(post._id, type, user.token);
    if (check === type) {
      setCheck();
      let index = reacts.findIndex((x) => x.react === check);
      if (index !== -1) {
        const updatedReacts = [...reacts];
        updatedReacts[index] = { ...updatedReacts[index], count: updatedReacts[index].count - 1 };
        setReacts(updatedReacts);
        setTotal((prev) => prev - 1);
      }
    } else {
      setCheck(type);
      let index = reacts.findIndex((x) => x.react === type);
      let index1 = reacts.findIndex((x) => x.react === check);
      if (index !== -1) {
        const updatedReacts = [...reacts];
        updatedReacts[index] = { ...updatedReacts[index], count: updatedReacts[index].count + 1 };
        setReacts(updatedReacts);
        setTotal((prev) => prev + 1);
      }
      if (index1 !== -1) {
        const updatedReacts = [...reacts];
        updatedReacts[index1] = { ...updatedReacts[index1], count: updatedReacts[index1].count - 1 };
        setReacts(updatedReacts);
        setTotal((prev) => prev - 1);
      }
    }
  };

  const showMore = () => {
    // reveal all non-deleted comments
    const totalVisible = (comments || []).filter((c) => !c?.isDeleted).length;
    setCount(totalVisible);
  };

  const postRef = useRef(null);
  const createCommentRef = useRef(null);

  const shareHandler = () => {
    setShowShareOptions((prev) => !prev);
  };

  // Helper to get tagged friends (populated or as IDs)
  const taggedFriends = post.taggedFriends && post.taggedFriends[0] && typeof post.taggedFriends[0] === 'object'
    ? post.taggedFriends
    : [];

  // Convert URLs in text to clickable links
  const renderTextWithLinks = (text) => {
    if (!text) return null;
    
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let urlIndex = 0;
    
    while ((match = urlRegex.exec(text)) !== null) {
      // Capture the URL value to avoid closure issues
      const url = match[0];
      const matchIndex = match.index;
      
      // Add text before URL
      if (matchIndex > lastIndex) {
        parts.push(text.substring(lastIndex, matchIndex));
      }
      // Add clickable link
      parts.push(
        <a
          key={`url-${urlIndex++}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="post_text_link"
          onClick={(e) => {
            e.stopPropagation(); // Prevent opening full screen
            e.preventDefault();
            if (url) {
              window.open(url, '_blank', 'noopener,noreferrer');
            }
          }}
        >
          {url}
        </a>
      );
      lastIndex = matchIndex + url.length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 0 ? parts : text;
  };

  // Handle post click to open full screen (but not on interactive elements)
  const handlePostClick = (e) => {
    if (disableFullScreen) return;

    const hasMedia = post.images && post.images.length > 0;
    const isLifeEvent = isLifeEventName(post.text);
    const feelingActivityOnly = (post.feeling || post.activity) && !hasMedia;

    // Don't open full screen for feeling/activity-only posts (unless it's also a life event)
    if (feelingActivityOnly && !isLifeEvent) {
      return;
    }

    // Don't open full screen if clicking on interactive elements
    if (
      e.target.closest('.post_action') ||
      e.target.closest('.post_header_right') ||
      e.target.closest('.post_menu') ||
      e.target.closest('.share_options_popup') ||
      e.target.closest('.reacts_popup') ||
      e.target.closest('.comment_input_wrap') ||
      e.target.closest('.comments_wrap') ||
      e.target.closest('a') ||
      e.target.closest('button') ||
      e.target.closest('video') ||
      e.target.closest('input') ||
      e.target.closest('textarea') ||
      e.target.closest('.tagged_icon_btn') ||
      e.target.closest('.tagged_friends_popover') ||
      e.target.closest('.post_text_link') ||
      e.target.closest('.feeling_display')
    ) {
      return;
    }

    // Open full screen for posts with images/videos OR for life events (with or without photo)
    if (hasMedia || isLifeEvent) {
      setShowFullScreen(true);
    }
  };

  return (
    <>
    <div
      className={`post ${gridView ? 'post_grid_view' : ''}`}
      style={{ ...(profile && { width: "100%" }), cursor: 'pointer' }}
      ref={postRef}
      onClick={handlePostClick}
    >
      {/* Show pinned badge only in profile view */}
      {profile && post.pinned && (
        <div className="pinned_badge" style={{
          display: 'flex', alignItems: 'center', gap: '6px', background: '#ffe066', color: '#333', fontWeight: 'bold', padding: '4px 12px', borderRadius: '16px', marginBottom: '8px', width: 'fit-content', boxShadow: '0 2px 8px rgba(0,0,0,0.07)'
        }}>
          <span role="img" aria-label="Pinned" style={{fontSize: '18px'}}>📌</span>
        </div>
      )}
      <div className="post_header">
        <Link
          to={`/profile/${post.user.username}`}
          className="post_header_left"
        >
          <img src={post.user.picture} alt="" />
          <div className="header_col">
            <div className="post_profile_name">
              {post.user.first_name} {post.user.last_name}
              {/* <div className="updated_p">
                {post.type === "profilePicture" &&
                  `updated ${
                    post.user.gender === "male" ? "his" : "her"
                  } profile picture`}
                {post.type === "coverPicture" &&
                  `updated ${
                    post.user.gender === "male" ? "his" : "her"
                  } cover picture`}
              </div> */}
            </div>
            {post.location && (
              <div className="post_location_header">
                <span className="location_address">
                  {post.location.address || "Location details"}
                </span>
              </div>
            )}
            <div className="post_profile_privacy_date">
              <Moment fromNow interval={30}>
                {post.createdAt}
              </Moment>
              {isGroupPost && groupName && (
                <>
                  {" "} • Posted in <strong>{groupName}</strong>
                </>
              )}
            </div>
          </div>
        </Link>
        {!(post.archived || post.deleted) && (
        <div
          className="post_header_right hover1"
          onClick={() => setShowMenu((prev) => !prev)}
        >
          <Dots color="#828387" />
        </div>
        )}
      </div>
      {post.background ? (
        <div
          className="post_bg"
          style={{ backgroundImage: `url(${post.background})` }}
        >
          <div className="post_bg_text">{renderTextWithLinks(post.text)}</div>
        </div>
      ) : post.type === null ? (
        <>
          {/* Show feeling/activity if present */}
          {(post.feeling || post.activity) && (
            <PostFeelingActivity 
              feeling={post.feeling} 
              activity={post.activity} 
              userName={post.user?.name || (post.user && [post.user.first_name, post.user.last_name].filter(Boolean).join(" ").trim()) || user?.name}
            />
          )}
          {/* Show life event card if it's a life event post without media */}
          {isLifeEventPost(post) ? (
            <div className="life_event_card">
              <div className="life_event_card_icon">
                {getLifeEventIcon(post.text.trim())}
              </div>
              <div className="life_event_card_title">
                {extractEventName(post.text.trim())}
              </div>
            </div>
          ) : (
            <>
          {/* Show images/videos */}
          {post.images && post.images.length > 0 && (
            <div
              className={
                post.images.length === 1
                  ? "grid_1"
                  : post.images.length === 2
                  ? "grid_2"
                  : post.images.length === 3
                  ? "grid_3"
                  : post.images.length === 4
                  ? "grid_4"
                  : post.images.length >= 5 && "grid_5"
              }
            >
              {post.images.slice(0, 5).map((image, i) =>
                image.type === "video" ? (
                  <div 
                    key={i} 
                    className={`video-container img-${i}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Request fullscreen for video
                      const videoElement = e.currentTarget.querySelector('video');
                      if (videoElement) {
                        if (videoElement.requestFullscreen) {
                          videoElement.requestFullscreen().catch(err => {
                            console.log('Error attempting to enable fullscreen:', err);
                          });
                        } else if (videoElement.webkitRequestFullscreen) {
                          videoElement.webkitRequestFullscreen();
                        } else if (videoElement.webkitEnterFullscreen) {
                          videoElement.webkitEnterFullscreen(); // iOS Safari
                        } else if (videoElement.mozRequestFullScreen) {
                          videoElement.mozRequestFullScreen();
                        } else if (videoElement.msRequestFullscreen) {
                          videoElement.msRequestFullscreen();
                        }
                      }
                    }}
                    style={{ cursor: 'pointer', position: 'relative' }}
                  >
                    <video
                      controls
                      className="post_video"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        // Request fullscreen for video
                        const video = e.currentTarget;
                        if (video.requestFullscreen) {
                          video.requestFullscreen().catch(err => {
                            console.log('Error attempting to enable fullscreen:', err);
                          });
                        } else if (video.webkitRequestFullscreen) {
                          video.webkitRequestFullscreen();
                        } else if (video.webkitEnterFullscreen) {
                          video.webkitEnterFullscreen(); // iOS Safari
                        } else if (video.mozRequestFullScreen) {
                          video.mozRequestFullScreen();
                        } else if (video.msRequestFullscreen) {
                          video.msRequestFullscreen();
                        }
                      }}
                    >
                      <source src={image.url} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                    <div className="video_fullscreen_indicator">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 3H5C3.89543 3 3 3.89543 3 5V8M21 8V5C21 3.89543 20.1046 3 19 3H16M16 21H19C20.1046 21 21 20.1046 21 19V16M3 16V19C3 20.1046 3.89543 21 5 21H8" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                  </div>
                ) : (
                  <img 
                    src={image.url} 
                    key={i} 
                    alt="" 
                    className={`img-${i}`}
                    onClick={(e) => {
                      if (disableFullScreen) {
                        e.stopPropagation();
                        setSelectedImageIndex(i);
                      }
                    }}
                    style={{ cursor: disableFullScreen ? 'pointer' : 'default' }}
                  />
                )
              )}
              {post.images.length > 5 && (
                <div className="more-pics-shadow">
                  +{post.images.length - 5}
                </div>
              )}
            </div>
          )}
          {/* Show life event label below photos if it's a life event post with media */}
          {post.images && post.images.length > 0 && isLifeEventName(post.text) && (
            <div className="life_event_label">
              <span className="life_event_label_icon">{getLifeEventIcon(post.text.trim())}</span>
              <span className="life_event_label_text">{extractEventName(post.text.trim())}</span>
            </div>
          )}
          {/* Show description text below images/videos (only if not a life event name and not feeling/activity-only) */}
          {post.text && post.text.trim() && !isLifeEventName(post.text) && !(post.feeling || post.activity) && (
            <div className="post_text">{renderTextWithLinks(post.text)}</div>
          )}
            </>
          )}
        </>
      ) : post.type === "profilePicture" ? (
        <div className="post_profile_wrap">
          <div className="post_updated_bg">
            <img src={post.user.cover} alt="" />
          </div>
          <img
            src={post.images[0].url}
            alt=""
            className="post_updated_picture"
          />
        </div>
      ) : post.type === "coverPicture" ? (
        <div className="post_cover_wrap">
          <img src={post.images[0].url} alt="" />
        </div>
      ) : null}

      {!(post.archived || post.deleted) && (
      <div className="post_infos">
        <div className="reacts_count">
          <div className="reacts_count_imgs">
            {reacts &&
              reacts
                .sort((a, b) => {
                  return b.count - a.count;
                })
                .slice(0, 3)
                .map(
                  (react, i) =>
                    react.count > 0 && (
                      <img
                        src={`../../../reacts/${react.react}.svg`}
                        alt=""
                        key={i}
                      />
                    )
                )}
          </div>
          <div className="reacts_count_num">{total > 0 && total}</div>
          {/* Tagged friends icon */}
          {taggedFriends.length > 0 && (
            <div className="tagged_icon_wrap">
              <button
                className="tagged_icon_btn"
                title="Show tagged friends"
                onClick={e => { e.stopPropagation(); setShowTaggedModal(prev => !prev); }}
                type="button"
              >
                <span role="img" aria-label="tagged" className="notranslate">👤</span>
              </button>
              {showTaggedModal && (
                <TaggedFriendsModal
                  taggedFriends={taggedFriends}
                  onClose={() => setShowTaggedModal(false)}
                />
              )}
            </div>
          )}
        </div>
        <div className="to_right">
          {/* Only count comments that are not soft-deleted */}
          <div className="comments_count">{(comments || []).filter(c => !c?.isDeleted).length} comments</div>
          <div className="share_count">0 share</div>
        </div>
      </div>
      )}
  {!(post.archived || post.deleted) && (
  <div className="post_actions">
        <ReactsPopup
          visible={visible}
          setVisible={setVisible}
          reactHandler={reactHandler}
        />
        <div
          className="post_action hover1"
          onMouseOver={() => {
            setTimeout(() => {
              setVisible(true);
            }, 500);
          }}
          onMouseLeave={() => {
            setTimeout(() => {
              setVisible(false);
            }, 500);
          }}
          onClick={() => {
            setVisible(false);
            reactHandler(check ? check : "like");
          }}
        >
          {check ? (
            <img
              src={`../../../reacts/${check}.svg`}
              alt=""
              className="small_react"
              style={{ width: "18px" }}
            />
          ) : (
            <i className="like_icon"></i>
          )}
          <span
            style={{
              color: `
          
          ${
            check === "like"
              ? "#4267b2"
              : check === "love"
              ? "#f63459"
              : check === "haha"
              ? "#f7b125"
              : check === "sad"
              ? "#f7b125"
              : check === "wow"
              ? "#f7b125"
              : check === "angry"
              ? "#e4605a"
              : ""
          }
          `,
            }}
          >
            {check ? check : "Like"}
          </span>
        </div>
        <div
          className="post_action hover1"
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            // When in fullscreen, comment input lives in viewer - use commentInputFocusRef
            const focusRef = commentInputFocusRef || createCommentRef;
            if (focusRef?.current?.focus) {
              focusRef.current.focus();
            } else {
              const input = postRef.current && postRef.current.querySelector('.comment_input_wrap input');
              if (input) input.focus();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              const focusRef = commentInputFocusRef || createCommentRef;
              if (focusRef?.current?.focus) focusRef.current.focus();
            }
          }}
        >
          <i className="comment_icon"></i>
          <span>Comment</span>
        </div>
        {/* Share button and popup wrapper */}
        <div className="post_action hover1" style={{ position: "relative" }}>
          <div onClick={shareHandler}>
            <i className="share_icon"></i>
            <span>Share</span>
          </div>
          {showShareOptions && (
            <div className="share_options_popup">
              <div style={{ fontWeight: "600", marginBottom: "12px" }}>
                Share this post
              </div>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <FaWhatsapp
                  size={20}
                  color="#25D366"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    const url = `${window.location.origin}/post/${post._id}`;
                    window.open(
                      `https://wa.me/?text=${encodeURIComponent(url)}`,
                      "_blank"
                    );
                  }}
                />
                <FaTwitter
                  size={20}
                  color="#1DA1F2"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    const url = `${window.location.origin}/post/${post._id}`;
                    window.open(
                      `https://twitter.com/intent/tweet?url=${encodeURIComponent(
                        url
                      )}`,
                      "_blank"
                    );
                  }}
                />
                {/* <FaInstagram
                  size={20}
                  color="#E4405F"
                  style={{ cursor: "pointer" }}
                /> */}
                <FaLinkedin
                  size={20}
                  color="#0077B5"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    const url = `${window.location.origin}/post/${post._id}`;
                    window.open(
                      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                        url
                      )}`,
                      "_blank"
                    );
                  }}
                />
                <FaTelegram
                  size={20}
                  color="#0088cc"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    const url = `${window.location.origin}/post/${post._id}`;
                    window.open(
                      `https://t.me/share/url?url=${encodeURIComponent(url)}`,
                      "_blank"
                    );
                  }}
                />
              </div>
              <div
                style={{
                  position: "absolute",
                  top: "8px",
                  right: "8px",
                  cursor: "pointer",
                }}
                onClick={() => setShowShareOptions(false)}
              >
                <FaTimes size={16} color="#888" />
              </div>
            </div>
          )}
        </div>
  </div>
  )}
      {/* Hide comments in grid view to save space or when hideComments is true */}
      {!gridView && !(post.archived || post.deleted) && !hideComments && (
        <div className="comments_wrap">
          <div className="comments_order"></div>
          <CreateComment
            ref={createCommentRef}
            user={user}
            postId={post._id}
            setComments={setComments}
            setCount={setCount}
          />
          {/* Compute visible comments once and reuse for rendering and checks */}
          {(() => {
            const visibleComments = (comments && Array.isArray(comments))
              ? comments.filter(c => !c?.isDeleted)
              : [];

            visibleComments.sort((a, b) => new Date(b.commentAt) - new Date(a.commentAt));

            return (
              <>
                {visibleComments.slice(0, count).map((comment) => (
                  <Comment
                    comment={comment}
                    key={comment._id || comment.id || comment.tempId}
                    postId={post._id}
                    setComments={setComments}
                    setCount={setCount}
                    onDelete={handleCommentDelete}
                  />
                ))}

                {visibleComments.length > 0 && count < visibleComments.length && (
                  <div className="view_comments" onClick={() => showMore()}>
                    View more comments
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}
      {showMenu && !(post.archived || post.deleted) && (
        <PostMenu
          userId={user.id}
          postUserId={post.user._id}
          imagesLength={post?.images?.length}
          setShowMenu={setShowMenu}
          postId={post._id}
          token={user.token}
          checkSaved={checkSaved}
          setCheckSaved={setCheckSaved}
          images={post.images}
          postRef={postRef}
          onPostDeleted={onPostDeleted}
          savedViewMenu={isSavedView}
          onUnsave={onUnsave}
        />
      )}
  {/* tagged friends popover now rendered inline next to the icon */}
    </div>
    
    {/* Image Fullscreen Modal */}
    {selectedImageIndex !== null && post.images && post.images[selectedImageIndex] && (
      <div 
        className="post_image_fullscreen_overlay" 
        onClick={() => setSelectedImageIndex(null)}
      >
        <div className="post_image_fullscreen_content" onClick={(e) => e.stopPropagation()}>
          <button 
            className="post_image_fullscreen_close" 
            onClick={() => setSelectedImageIndex(null)}
            aria-label="Close"
          >
            <FaTimes />
          </button>
          <div className="post_image_fullscreen_wrapper">
            {post.images[selectedImageIndex].type === "video" ? (
              <video
                controls
                autoPlay
                className="post_image_fullscreen_video"
                onClick={(e) => e.stopPropagation()}
              >
                <source src={post.images[selectedImageIndex].url} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <img 
                src={post.images[selectedImageIndex].url} 
                alt="Fullscreen" 
                className="post_image_fullscreen_img"
                onClick={(e) => e.stopPropagation()}
              />
            )}
            {post.images.length > 1 && (
              <>
                <button
                  className="post_image_fullscreen_nav post_image_fullscreen_prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImageIndex((prev) => 
                      prev > 0 ? prev - 1 : post.images.length - 1
                    );
                  }}
                  aria-label="Previous image"
                >
                  ‹
                </button>
                <button
                  className="post_image_fullscreen_nav post_image_fullscreen_next"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImageIndex((prev) => 
                      prev < post.images.length - 1 ? prev + 1 : 0
                    );
                  }}
                  aria-label="Next image"
                >
                  ›
                </button>
                <div className="post_image_fullscreen_counter">
                  {selectedImageIndex + 1} / {post.images.length}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    )}
    
    {/* Full Screen Post Viewer */}
    {showFullScreen && (
      <FullScreenPostViewer
        post={post}
        user={user}
        onClose={() => setShowFullScreen(false)}
        onPostDeleted={onPostDeleted}
        isGroupPost={isGroupPost}
        groupName={groupName}
      />
    )}
    </>
  );
}
